import { FeatureFlag } from '@deltasierra/features-feature-flags-core';
import { AgencyId } from '../agency/Agency';
import { Client } from '../client/Client';
import { ClientId } from '../client/ClientId';
import { PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';
import { EmailAddress } from '../common/lang/web';
import { FullAuditable, HasId } from '../common/model/traits';
import { GetLinkedInPageNameAndOrganizationUrnResponse, LinkedIn } from '../integration';
import {
    GoogleMyBusinessAccountResourceId,
    GoogleMyBusinessLocationResourceId,
} from '../integration/integrations/googleMyBusiness';
import { LinkedInOrganizationUrn } from '../integration/integrations/linkedIn/models/organization';
import { Upload } from '../upload/Upload';
import { InviteClientUserDto, SanitizedUser, User, UserId } from '../user/User';
import { LocationCategoryOption, LocationCategoryOptionId } from './LocationCategoryOption';
import { LocationId } from './LocationId';
import { LocationMap } from './LocationMap';

export interface LocationIntegrationFields {
    campaignMonitorClientId: string | null;
    facebookPageId: string | null;
    facebookPageName: string | null;
    fitwareAccountId: number | null;
    fitwareClubBrand: string | null;
    fitwareClubEmail: EmailAddress | null;
    fitwareClubName: string | null;
    fitwareClubNumber: string | null;
    googleAdWordsAccountId: string | null;
    googleAdWordsCampaignName: string | null;
    googleAdWordsCustomerId: string | null;
    googleAnalyticsPagePath: string | null;
    googleAnalyticsPropertyId: string | null;
    googleAnalyticsViewId: string | null;
    googleMyBusinessAccountName: GoogleMyBusinessAccountResourceId | null;
    googleMyBusinessLocationId: GoogleMyBusinessLocationResourceId | null;
    gymSalesCompanyId: string | null;
    gymSalesCompanyName: string | null;
    instagramPageId: string | null;
    instagramPageName: string | null;
    instagramFacebookPageId: string | null;
    instagramFacebookPageName: string | null;
    linkedInOrganizationUrn: LinkedInOrganizationUrn | null;
    mailchimpServer: string | null;
}

export interface Location extends HasId<LocationId>, LocationIntegrationFields, FullAuditable {
    title: string;
    displayName?: string | null;
    clientId: ClientId;
    timezone: string;
    signatoryName: string | null;
    signatoryEmail: string | null;
    phoneNumber?: string;
    locationEmail?: string | null;
    mapId?: number;
    addressLineOne?: string;
    addressLineTwo?: string;
    addressLineThree?: string;
    shippingAttention: string | null;
    shippingCompanyName: string | null;
    shippingLineOne: string | null;
    shippingLineTwo: string | null;
    shippingLineThree: string | null;
    supportedPlannerLimit: number | null;
    websiteUrl?: string | null;
    latitude: number | null;
    longitude: number | null;
    collectionLimit?: number | null;

    categories?: LocationCategoryOption[];
    client?: Client;
    logo?: Upload;
    map?: LocationMap;
    users?: User[];
}

export type LocationShippingDetails = Pick<
    Location,
    | 'id'
    | 'shippingAttention'
    | 'shippingCompanyName'
    | 'shippingLineOne'
    | 'shippingLineThree'
    | 'shippingLineTwo'
    | 'title'
>;

export type LocationIdentityFields = Pick<Location, 'clientId' | 'displayName' | 'id' | 'title'>;

/**
 * @deprecated DS-2559: Remove this once the mobile app no longer make use of it.
 */
export type AssignedLocationsView = LocationIdentityFields &
    LocationIntegrationFields &
    Pick<
        Location,
        | 'addressLineOne'
        | 'addressLineThree'
        | 'addressLineTwo'
        | 'categories'
        | 'latitude'
        | 'locationEmail'
        | 'logo'
        | 'longitude'
        | 'map'
        | 'phoneNumber'
        | 'shippingAttention'
        | 'shippingCompanyName'
        | 'shippingLineOne'
        | 'shippingLineThree'
        | 'shippingLineTwo'
        | 'supportedPlannerLimit'
        | 'timezone'
        | 'websiteUrl'
    > & {
        client: string; // TODO: Rename to clientTitle
        agencyId: AgencyId;
        agencyHasGoogleAdWordsMarketingContact: boolean;
        timeOffset: string;
    };

export type AssignedLocation = LocationIdentityFields &
    LocationIntegrationFields &
    Pick<
        Location,
        | 'addressLineOne'
        | 'addressLineThree'
        | 'addressLineTwo'
        | 'collectionLimit'
        | 'latitude'
        | 'locationEmail'
        | 'logo'
        | 'longitude'
        | 'map'
        | 'phoneNumber'
        | 'shippingAttention'
        | 'shippingCompanyName'
        | 'shippingLineOne'
        | 'shippingLineThree'
        | 'shippingLineTwo'
        | 'supportedPlannerLimit'
        | 'timezone'
        | 'websiteUrl'
    > & {
        client: string; // TODO: Rename to clientTitle
        agencyId: AgencyId;
        agencyName: string;
        timeOffset: string;
        graphqlId: string;
        clientGraphqlId: string;
        agencyGraphqlId: string;
        clientFeatures?: FeatureFlag[];
    };

/**
 * Used by the location picker
 */
export type LocationPickerDto = LocationIdHierarchy & {
    categories: Array<LocationCategoryOption['id']>;
};

export type LocationsAdminView = Pick<
    Location,
    'clientId' | 'displayName' | 'id' | 'signatoryEmail' | 'signatoryName' | 'timezone' | 'title' | 'users'
> & {
    client: string;
    timeOffset: string;
    invited: boolean;
    signedUp: boolean;
};

export interface BaseLocationIdHierarchy {
    agencyId: AgencyId;
    agencyTitle: string;
    clientId: ClientId;
    clientTitle: string;
    locationId: LocationId;
    locationTitle: string;
}

export interface LocationIdHierarchy extends BaseLocationIdHierarchy {
    agencyGraphqlId: string;
    clientGraphqlId: string;
    locationGraphqlId: string;
}

export interface MobileLocationIdHierarchy {
    title: string;
    id: string;
    legacyId: LocationId;
    client: {
        title: string;
        id: string;
        legacyId: ClientId;
    };
}

export function assignedLocationToLocationIdHierarchy(location: AssignedLocation): LocationIdHierarchy {
    return {
        agencyGraphqlId: location.agencyGraphqlId,
        agencyId: location.agencyId,
        agencyTitle: location.agencyName,
        clientGraphqlId: location.clientGraphqlId,
        clientId: location.clientId,
        clientTitle: location.client,
        locationGraphqlId: location.graphqlId,
        locationId: location.id,
        locationTitle: location.title,
    };
}
export interface LocationTimeOffsetDto {
    id: LocationId;
    clientId: ClientId;
    timeOffset: string;
    title: string;
}

export interface LocationFacebookPageDto {
    id: LocationId;
    facebookPageId: string;
    facebookPageName: string;
}

interface BaseLocationApiParams {
    locationId: LocationId;
}

export interface GetAssignedLocationsQuery {
    userId?: UserId;
}

export interface MultipleLocationsBody {
    locationIds: LocationId[];
}

export interface UpdateFitwareClubBody {
    fitwareAccountId: number | null;
    fitwareClubBrand: string | null;
    fitwareClubNumber: string | null;
    fitwareClubName: string | null;
    fitwareClubEmail: EmailAddress | null;
}

export interface UpdateFour51CredentialsBody {
    username: string;
    password: string;
}

interface UpdateCategoriesBody {
    categoryIds: LocationCategoryOptionId[];
}

export interface UpdateFacebookPageBody {
    facebookPageId: string | null;
    facebookPageName: string | null;
}

export type NewLocationDto = Pick<Location, 'timezone' | 'title'>;

export type UpdateLocationDto = Pick<Location, 'displayName' | 'supportedPlannerLimit' | 'timezone' | 'title'>;

export type UpdateGoogleMyBusinessLocationConfigBody = Pick<
    Location,
    'googleMyBusinessAccountName' | 'googleMyBusinessLocationId'
>;

export type UpdateLinkedInLocationConfigBody = Pick<Location, 'linkedInOrganizationUrn'>;

export const LocationApi = {
    createLocation: new ApiRoute<Location, { clientId: ClientId }, NewLocationDto, void>(HttpMethod.Post, path =>
        path.literal('clients').param('clientId').literal('locations'),
    ),
    deleteFour51Credentials: new ApiRoute<void, BaseLocationApiParams, void, void>(HttpMethod.Delete, path =>
        path.literal('location').param('locationId').literal('four51'),
    ),
    getAssignedLocation: new ApiRoute<AssignedLocation, BaseLocationApiParams, void, void>(HttpMethod.Get, path =>
        path.literal('location').param('locationId'),
    ),
    /**
     * @deprecated DS-2559: Remove this once the mobile app no longer make use of it.
     */
    getAssignedLocations: new ApiRoute<AssignedLocationsView[], void, void, void>(HttpMethod.Get, path =>
        path.literal('locations'),
    ),
    /**
     * Used in publishing, to display nice LinkedIn page names.
     * Only returns results for locations that have a LinkedIn organization URN populated.
     */
    getConfiguredPageNameAndOrganizationUrn: new ApiRoute<
        GetLinkedInPageNameAndOrganizationUrnResponse,
        { locationId: LocationId },
        void,
        void
    >(HttpMethod.Get, path => path.literal('location').literal(LinkedIn.name).literal('location').param('locationId')),
    /**
     * Used in publishing, to display nice Facebook page names.
     * Only returns results for locations that have a FB page ID & name populated.
     */
    getFacebookPageForLocations: new ApiRoute<LocationFacebookPageDto[], void, MultipleLocationsBody, void>(
        HttpMethod.Post,
        path => path.literal('locations').literal('facebookPage'),
    ),
    /**
     * Used for location category admin.
     */
    getLocationCategories: new ApiRoute<LocationCategoryOption[], BaseLocationApiParams, void, void>(
        HttpMethod.Get,
        path => path.literal('location').param('locationId').literal('categories'),
    ),
    /**
     * Returns minimal location hierarchy info.
     */
    getSimpleAssignedLocations: new ApiRoute<LocationIdHierarchy[], void, void, GetAssignedLocationsQuery | void>(
        HttpMethod.Get,
        path => path.literal('locations').literal('assigned'),
    ),
    /**
     * Used by the Location Picker.
     */
    getSimpleAssignedLocationsWithCategories: new ApiRoute<
        LocationPickerDto[],
        void,
        void,
        GetAssignedLocationsQuery | void
    >(HttpMethod.Get, path => path.literal('locations').literal('assignedWithCategories')),
    updateFacebookPage: new ApiRoute<void, BaseLocationApiParams, UpdateFacebookPageBody, void>(HttpMethod.Post, path =>
        path.literal('location').param('locationId').literal('facebookPage'),
    ),
    updateFour51Credentials: new ApiRoute<void, BaseLocationApiParams, UpdateFour51CredentialsBody, void>(
        HttpMethod.Put,
        path => path.literal('location').param('locationId').literal('four51'),
    ),
    updateLocation: new ApiRoute<Location, BaseLocationApiParams, UpdateLocationDto, void>(HttpMethod.Post, path =>
        path.literal('location').param('locationId'),
    ),
    updateLocationCategories: new ApiRoute<void, BaseLocationApiParams, UpdateCategoriesBody, void>(
        HttpMethod.Post,
        path => path.literal('location').param('locationId').literal('categories'),
    ),
};

interface LocationUsersParams {
    clientId: ClientId;
    locationId: LocationId;
}

export interface ResendLocationUsersInviteParams extends LocationUsersParams {
    userId: UserId;
}

const addLocationUsersApiRoot = <T extends LocationUsersParams>(path: PathBuilder<T>) =>
    path.literal('clients').param('clientId').literal('locations').param('locationId').literal('users');

export const LocationUsersApi = {
    getAssignableUsers: new ApiRoute<User[], LocationUsersParams, void, void>(HttpMethod.Get, path =>
        addLocationUsersApiRoot(path).literal('assignable'),
    ),
    getUsers: new ApiRoute<SanitizedUser[], LocationUsersParams, void, void>(HttpMethod.Get, addLocationUsersApiRoot),
    inviteClientUser: new ApiRoute<SanitizedUser, LocationUsersParams, InviteClientUserDto, void>(
        HttpMethod.Post,
        path => addLocationUsersApiRoot(path).literal('invite'),
    ),
};

export { LocationId } from './LocationId';

export const DEFAULT_LOCATION_COLLECTION_LIMIT = 30;
